import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import {Am5Settings} from "@/mixins/am5charts/Am5Settings";
import {markRaw} from "vue";
import * as _ from "lodash";

const DEFAULT_MAX = -100000
const DEFAULT_MIN = 100000

export const Am5SideAxis = class {
    constructor(root) {
        // Owns a live am5 ValueAxis and its series — never let Vue proxy it.
        // See the note in Am5Series' constructor for why a second identity
        // strands canvas displays and produces ghost series.
        markRaw(this)
        this.create(root)
    }

    addSeries(series, loadData = false) {
        if (_.isNil(series)) {
            console.error(`Am5SideAxis@addSeries - Unable to add null series.`)
            return
        }

        if (_.find(this.series, series)) {
            return
        }

        if (this.axisInitialized()) {
            this.axis.set("visible", true)
        }

        if (loadData) {
            series.loadData()
        }

        this.series.push(series)
        series.setSideAxis(this)
        series.setupSeries()
    }

    removeSeries(series) {
        if (_.isNil(series) && this.axisInitialized() && this.axis.series.length === 0) {
            return
        }

        if (!this.hasSeriesVisible(series)) {
            return
        }

        const removed = _.remove(this.series, s => {
            return `${s.id}` === `${series.id}`
        })

        if (removed.length === 0) {
            return
        }

        series.removeSeries()

        if (this.axisInitialized() && this.series.length === 0) {
            this.axis.set("visible", false)
        }
    }

    getCurrentProfile() {
        return this.currentProfile
    }

    setCurrentProfile(profile) {
        this.currentProfile = profile
        if (!this.axisInitialized()) {
            return
        }

        this._updateAxisTitle()
    }

    hasSeriesVisible(series) {
        if (!series.isVisible()) {
            return false
        }

        return _.findIndex(this.series, s => {
            return s.id === series.id
        }) !== -1
    }

    checkScalingProfile(profileId) {
        if (_.isNil(this.currentProfile)) {
            return false
        }

        return profileId === this.currentProfile.id
    }

    updateMinMax() {
        this.min = DEFAULT_MIN
        this.max = DEFAULT_MAX
        _.each(this.series, s => {
            this.min = Math.min(this.min, s.min)
            this.max = Math.max(this.max, s.max)
        })

        this.renderAxis()
    }

    getAxis() {
        return this.axis
    }

    renderLeft() {
        this.isLeft = true
        if (this.axisInitialized()) {
            this.axis.get("renderer").set("opposite", false)
        }

        _.each(this.series, series => {
            series.setAxisLeft()
        })
    }

    renderRight() {
        this.isLeft = false
        if (this.axisInitialized()) {
            this.axis.get("renderer").set("opposite", true)
        }

        _.each(this.series, series => {
            series.setAxisRight(this.axisIndex)
        })
    }

    buildAndRender(chart) {
        if (this.axisInitialized()) {
            this.dispose()
        }

        this.axis = chart.yAxes.push(
            am5xy.ValueAxis.new(this.root, {
                extraMin: Am5Settings.Y_AXIS_SPACE_PERCENT,
                extraMax: Am5Settings.Y_AXIS_SPACE_PERCENT,
                includeRangesInMinMax: true,
                renderer: am5xy.AxisRendererY.new(this.root, {
                    minWidth: 35,
                }),
            })
        )

        this.axisIndex = chart.yAxes.length - 1

        // If this is not the left axis, sync it with the left axis
        if (this.axisIndex !== 0) {
            this.axis.set("syncWithAxis", chart.yAxes.getIndex(0))
        }

        // Set profile information for axis title
        if (!_.isNil(this.currentProfile)) {
            this._updateAxisTitle()
        }

        if (!this.isLeft) {
            this.renderRight()
        }

        this.renderAxis()
    }

    renderAxis() {
        if (_.isNil(this.axis)) {
            return
        }

        if (this.min === this.max) {
            this.min -= 0.25
            this.max += 0.25
        }

        // Explicitly set axis min/max to include threshold values
        // am5's includeRangesInMinMax doesn't reliably expand axis for single-value ranges
        if (this.min < DEFAULT_MIN && this.max > DEFAULT_MAX) {
            let range = this.max - this.min
            let padding = range * Am5Settings.Y_AXIS_SPACE_PERCENT
            this.axis.setAll({
                min: this.min - padding,
                max: this.max + padding,
                strictMinMax: true,
            })
        }

        // Disable tooltip on Y axis
        this.axis.set("tooltip", am5.Tooltip.new(this.root, { forceHidden: true }))

        if (this.currentProfile && this.currentProfile.has_text_labels_on_graph) {
            let text_labels_for_graph = this.currentProfile.text_labels_for_graph;
            if (typeof text_labels_for_graph === 'string') {
                text_labels_for_graph = JSON.parse(text_labels_for_graph);
            }
            this.numberToTextMap = text_labels_for_graph;

            // Hide default labels and grid
            this.axis.get("renderer").labels.template.set("opacity", 0)
            this.axis.get("renderer").grid.template.set("opacity", 0)

            // Only build the labeled ranges once. Re-clearing/recreating them on every
            // renderAxis() call (which happens whenever a series is added/removed) creates
            // a churn that races with am5's pending opposite=true layout pass and triggers
            // "EventDispatcher is disposed" inside AxisRendererY._changed.
            if (this._labeledRanges.length === 0) {
                Object.keys(this.numberToTextMap).forEach(number => {
                    let dataItem = this.axis.makeDataItem({ value: Number(number) })
                    let range = this.axis.createAxisRange(dataItem)
                    range.get("label").setAll({
                        text: this.numberToTextMap[number],
                        opacity: 1,
                    })
                    range.get("grid").setAll({
                        strokeOpacity: 1,
                        stroke: am5.color("#ccc"),
                    })
                    this._labeledRanges.push(range)
                });
            }
        } else {
            // Reset to default settings
            this.axis.get("renderer").labels.template.set("opacity", 1)
            this.axis.get("renderer").grid.template.set("opacity", 1)
        }
    }

    axisInitialized() {
        return !_.isNil(this.axis)
    }

    _updateAxisTitle() {
        if (_.isNil(this.currentProfile) || !this.axisInitialized()) {
            return
        }

        let titleText = this.currentProfile.units.trim() === ""
            ? `${this.currentProfile.name}`
            : `${this.currentProfile.name} (${this.currentProfile.units})`

        // In am5, axis titles are set differently - we use a Label on the axis
        if (this._titleLabel && !this._titleLabel.isDisposed()) {
            this._titleLabel.set("text", titleText)
        } else {
            this._titleLabel = this.axis.children.unshift(
                am5.Label.new(this.root, {
                    text: titleText,
                    rotation: -90,
                    y: am5.p50,
                    centerX: am5.p50,
                    fontSize: 12,
                })
            )
        }
    }

    _clearAxisRanges() {
        if (_.isNil(this.axis)) {
            return
        }
        // Only remove the labeled ranges this side-axis owns; threshold ranges
        // owned by Am5Series live on the same axisRanges list and would otherwise
        // be collateral damage.
        _.each(this._labeledRanges, range => {
            this.axis.axisRanges.removeValue(range)
        })
        this._labeledRanges = []
    }

    create(root) {
        this.root = root
        this.series = []
        this.axisIndex = null
        this.isLeft = true
        this.min = DEFAULT_MIN
        this.max = DEFAULT_MAX
        this.currentProfile = null

        this.axis = null
        this._titleLabel = null
        this.numberToTextMap = {};
        this._labeledRanges = []
    }

    dispose() {
        if (this.axisInitialized()) {
            this.axis.dispose()
            this.axis = null
        }
        this._labeledRanges = []
    }
}
